import { useCallback } from 'react';
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthContext';
import {
  NOTIFICATIONS_KEY,
  UNREAD_KEY,
  listKey,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  unreadCount,
  type AppNotification,
  type NotificationPage,
} from './notifications.api';

/**
 * The bell's poll. One small read (the unread count, no rows) a minute while
 * the tab is in view; a hidden tab stops polling (React Query's interval
 * pauses in the background) and catches up on focus. One shared cache entry,
 * so the bell in the shell and the one in the Employee Hub never double it
 * (the useNavBadges lesson, 2026-09-22).
 */
export const POLL_MS = 60_000;
const STALE_MS = 30_000;
/** How many notices the popover shows. */
export const POPOVER_LIMIT = 8;
/** A page of the full inbox. */
export const PAGE_LIMIT = 30;

/** Agents (the Agent Portal) have no staff inbox; everyone else does, in every tenant. */
export function useInboxEnabled(): boolean {
  const { user } = useAuth();
  return !!user && user.role !== 'agent';
}

export function useUnreadCount(enabled: boolean): number {
  const { data } = useQuery({
    queryKey: UNREAD_KEY,
    queryFn: ({ signal }) => unreadCount(signal),
    enabled,
    staleTime: STALE_MS,
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    // A count is decoration: a failed poll keeps the last good number.
    retry: false,
    placeholderData: (prev) => prev,
  });
  return data ?? 0;
}

/**
 * The popover's short list: fetched when it opens. The bell marks it stale
 * when the count goes up, so the next open shows the new notice.
 */
export function useRecentNotifications(open: boolean) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: listKey(POPOVER_LIMIT),
    queryFn: async ({ signal }) => {
      const page = await listNotifications({ limit: POPOVER_LIMIT }, signal);
      qc.setQueryData(UNREAD_KEY, page.unreadCount ?? 0);
      return page;
    },
    enabled: open,
    staleTime: STALE_MS,
    retry: false,
    placeholderData: (prev) => prev,
  });
}

/** The full inbox, newest first, a page at a time. */
export function useNotificationPages(enabled: boolean, unreadOnly: boolean) {
  const qc = useQueryClient();
  return useInfiniteQuery({
    queryKey: [...NOTIFICATIONS_KEY, 'pages', unreadOnly ? 'unread' : 'all'],
    queryFn: async ({ pageParam, signal }) => {
      const page = await listNotifications({ limit: PAGE_LIMIT, before: pageParam, unread: unreadOnly }, signal);
      qc.setQueryData(UNREAD_KEY, page.unreadCount ?? 0);
      return page;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.hasMore && last.data.length ? last.data[last.data.length - 1]!.created_at : undefined),
    enabled,
    staleTime: STALE_MS,
    refetchOnWindowFocus: true,
    retry: false,
  });
}

type Cached = number | NotificationPage | InfiniteData<NotificationPage> | undefined;

/** Apply a read to every cached copy (count, popover list, pages), so the UI moves before the server answers. */
function markInCaches(qc: QueryClient, isHit: (n: AppNotification) => boolean, readAt: string) {
  const mark = (rows: AppNotification[]) =>
    rows.map((n) => {
      if (n.read_at || !isHit(n)) return n;
      return { ...n, read_at: readAt };
    });
  qc.setQueriesData<Cached>({ queryKey: NOTIFICATIONS_KEY }, (old) => {
    if (old == null || typeof old === 'number') return old;
    if ('pages' in old) {
      return { ...old, pages: old.pages.map((p) => ({ ...p, data: mark(p.data) })) };
    }
    return { ...old, data: mark(old.data) };
  });
}

export function useNotificationActions() {
  const qc = useQueryClient();

  const markRead = useCallback(
    async (n: AppNotification) => {
      if (n.read_at) return;
      markInCaches(qc, (x) => x.id === n.id, new Date().toISOString());
      qc.setQueryData<number>(UNREAD_KEY, (c) => Math.max(0, (c ?? 0) - 1));
      try {
        await markNotificationRead(n.id);
      } catch {
        // The next poll tells the truth; a failed mark is not worth a toast.
      }
      void qc.invalidateQueries({ queryKey: UNREAD_KEY });
    },
    [qc],
  );

  const markAllRead = useCallback(async () => {
    markInCaches(qc, () => true, new Date().toISOString());
    qc.setQueryData<number>(UNREAD_KEY, 0);
    try {
      await markAllNotificationsRead();
    } finally {
      void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    }
  }, [qc]);

  return { markRead, markAllRead };
}
